/** @format */

import {
  ArrowLeft,
  CheckCircle2,
  ImagePlus,
  Loader2,
  ScanSearch,
  Trash2,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { rmaService, RMACriteria, RMAVerdict } from "../../api/rmaService";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import Header from "../../components/ui/Header";
import Toast from "../../components/ui/Toast";
import { useOrgRole } from "../../hooks/useOrgRole";
import { useToast } from "../../hooks/useToast";
import { useSopStore } from "../../store/useSopStore";
import { uploadFileToS3 } from "../../utils/uploadFileToS3";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE_MB = 10;

type CriteriaLightboxItem = RMACriteria;

interface PendingCriteriaUpload {
  localId: string;
  file: File;
  previewUrl: string;
  context: string;
}

interface CriteriaDatasetColumnProps {
  verdict: RMAVerdict;
  items: RMACriteria[];
  pageSize: number;
  visibleCount: number;
  onDelete: (criteriaId: string) => void;
  onOpen: (item: CriteriaLightboxItem) => void;
  onShowMore: () => void;
}

function makeId() {
  return Math.random().toString(36).slice(2);
}

function validateFile(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type))
    return "Unsupported format. Use JPEG, PNG, or WebP.";
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024)
    return `File too large (max ${MAX_FILE_SIZE_MB} MB).`;
  return null;
}

const CriteriaDatasetColumn: FC<CriteriaDatasetColumnProps> = ({
  verdict,
  items,
  pageSize,
  visibleCount,
  onDelete,
  onOpen,
  onShowMore,
}) => {
  const isPass = verdict === "pass";
  const borderCard = isPass ? "border-green-500/30" : "border-red-500/30";
  const showCount = Math.min(visibleCount, items.length);
  const canShowMore = items.length > showCount;

  return (
    <div className="flex flex-col gap-3 flex-1 min-w-0">
      {items.length === 0 ? (
        <div className={`rounded-xl border ${borderCard} bg-slate-800/30 px-4 py-5`}>
          <p className="text-sm text-slate-200 font-semibold">
            No {isPass ? "PASS" : "FAIL"} examples yet
          </p>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Add examples to build a reliable dataset.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-700/40 bg-slate-900/40">
          <div className="grid grid-cols-5 md:grid-cols-7 gap-2 p-3 max-h-[420px] overflow-auto">
            {items.slice(0, showCount).map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onOpen(item)}
                className={`relative rounded-lg overflow-hidden border ${item.reason ? borderCard : "border-amber-500/40"} bg-slate-800/40 hover:bg-slate-800/60 transition-colors group`}
                title={item.reason ?? undefined}
              >
                {item.url ? (
                  <img
                    src={item.url}
                    alt={`${verdict} criteria`}
                    className="w-full aspect-square object-cover group-hover:opacity-90 transition-opacity"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full aspect-square bg-slate-700/40 flex items-center justify-center">
                    <ImagePlus className="w-5 h-5 text-slate-500" />
                  </div>
                )}
                <span className="absolute top-1 right-1">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item.id);
                    }}
                    className="w-7 h-7 rounded-full bg-black/60 hover:bg-red-600/90 border border-white/10 flex items-center justify-center transition-colors"
                    aria-label="Delete criteria"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-white" />
                  </button>
                </span>
              </button>
            ))}
          </div>
          {canShowMore && (
            <div className="px-3 pb-3">
              <button
                type="button"
                onClick={onShowMore}
                className="w-full px-3 py-2 rounded-lg border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-600 transition-colors text-sm"
              >
                Show {Math.min(pageSize, items.length - showCount)} more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RMADatasetPage: FC = () => {
  const { sopId } = useParams<{ sopId: string }>();
  const navigate = useNavigate();
  const { canManageRMA, isRoleResolving } = useOrgRole();
  const { fetchSOPById, sops } = useSopStore();
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    if (sopId && !sops?.find((s) => s.sopId === sopId)) {
      fetchSOPById(sopId).catch(() => {});
    }
  }, [sopId, sops, fetchSOPById]);

  const [criteriaBusy, setCriteriaBusy] = useState(false);
  const [criteriaLoading, setCriteriaLoading] = useState(true);
  const [criteria, setCriteria] = useState<RMACriteria[]>([]);
  const [criteriaError, setCriteriaError] = useState<string | null>(null);
  const [criteriaDetail, setCriteriaDetail] = useState<CriteriaLightboxItem | null>(null);
  const [criteriaReasonDraft, setCriteriaReasonDraft] = useState("");
  const [criteriaReasonBusy, setCriteriaReasonBusy] = useState(false);
  const [selectedCriteriaVerdict, setSelectedCriteriaVerdict] = useState<RMAVerdict>("pass");
  const [addCriteriaModalOpen, setAddCriteriaModalOpen] = useState(false);
  const [pendingCriteriaUploads, setPendingCriteriaUploads] = useState<PendingCriteriaUpload[]>([]);
  const [addCriteriaBusy, setAddCriteriaBusy] = useState(false);
  const [passVisible, setPassVisible] = useState(50);
  const [failVisible, setFailVisible] = useState(50);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [deleteConfirmTargetId, setDeleteConfirmTargetId] = useState<string | null>(null);
  const [discardConfirmTarget, setDiscardConfirmTarget] = useState<"add" | "detail" | null>(
    null,
  );

  const criteriaInputRef = useRef<HTMLInputElement>(null);
  const canEditDataset = canManageRMA;

  const passCriteria = useMemo(
    () => criteria.filter((c) => c.verdict === "pass"),
    [criteria],
  );
  const failCriteria = useMemo(
    () => criteria.filter((c) => c.verdict === "fail"),
    [criteria],
  );
  const visibleCriteria = selectedCriteriaVerdict === "pass" ? passCriteria : failCriteria;
  const hasPendingUploadChanges = pendingCriteriaUploads.length > 0;
  const canSavePendingCriteriaUploads =
    !addCriteriaBusy &&
    pendingCriteriaUploads.length > 0 &&
    pendingCriteriaUploads.every((item) => item.context.trim().length > 0);
  const hasCriteriaReasonChanges = criteriaDetail
    ? criteriaReasonDraft.trim() !== (criteriaDetail.reason ?? "").trim()
    : false;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!sopId) {
        setCriteria([]);
        setCriteriaLoading(false);
        setCriteriaError(null);
        return;
      }
      setCriteriaLoading(true);
      setCriteriaError(null);
      try {
        const res = await rmaService.getCriteria(sopId);
        if (cancelled) return;
        setCriteria(res);
        setPassVisible(50);
        setFailVisible(50);
      } catch (err) {
        if (cancelled) return;
        setCriteriaError(
          err instanceof Error ? err.message : "Failed to load criteria dataset.",
        );
      } finally {
        if (!cancelled) setCriteriaLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [sopId]);

  useEffect(() => {
    setCriteriaReasonDraft(criteriaDetail?.reason ?? "");
  }, [criteriaDetail]);

  const appendCriteriaFiles = useCallback((files: FileList) => {
    const valid = Array.from(files)
      .map((file) => ({ file, error: validateFile(file) }))
      .filter((item) => !item.error)
      .map((item) => item.file);

    if (!valid.length) {
      setGlobalError("No valid images selected.");
      return;
    }

    setGlobalError(null);
    setPendingCriteriaUploads((prev) => [
      ...prev,
      ...valid.map((file) => ({
        localId: makeId(),
        file,
        previewUrl: URL.createObjectURL(file),
        context: "",
      })),
    ]);
  }, []);

  const openAddCriteriaModal = useCallback(() => {
    setGlobalError(null);
    setPendingCriteriaUploads([]);
    setAddCriteriaModalOpen(true);
  }, []);

  const closeAddCriteriaModal = useCallback(() => {
    setPendingCriteriaUploads((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
    setAddCriteriaModalOpen(false);
    setAddCriteriaBusy(false);
  }, []);

  const requestCloseAddCriteriaModal = useCallback(
    (source: "dismiss" | "button") => {
      if (addCriteriaBusy) return;
      if (source === "dismiss" && hasPendingUploadChanges) {
        setDiscardConfirmTarget("add");
        return;
      }
      closeAddCriteriaModal();
    },
    [addCriteriaBusy, closeAddCriteriaModal, hasPendingUploadChanges],
  );

  const savePendingCriteriaUploads = useCallback(async () => {
    if (!sopId) return;
    if (!pendingCriteriaUploads.length) {
      setGlobalError("Select at least one image.");
      return;
    }
    const missing = pendingCriteriaUploads.some((item) => !item.context.trim());
    if (missing) {
      setGlobalError("Context is required for every selected image.");
      return;
    }

    setGlobalError(null);
    setCriteriaBusy(true);
    setAddCriteriaBusy(true);
    try {
      for (const item of pendingCriteriaUploads) {
        const res = await uploadFileToS3(item.file, sopId);
        if (!res || typeof res === "string") throw new Error("Upload failed");
        const saved = await rmaService.addCriteria(
          sopId,
          res.media_id,
          selectedCriteriaVerdict,
          item.context.trim(),
        );
        setCriteria((prev) => [...prev, saved]);
      }
      closeAddCriteriaModal();
    } catch (err) {
      setGlobalError(
        err instanceof Error ? err.message : "Failed to add criteria images.",
      );
      setAddCriteriaBusy(false);
    } finally {
      setCriteriaBusy(false);
    }
  }, [closeAddCriteriaModal, pendingCriteriaUploads, selectedCriteriaVerdict, sopId]);

  const saveCriteriaReason = useCallback(async () => {
    if (!sopId || !criteriaDetail) return;
    setCriteriaReasonBusy(true);
    try {
      const updated = await rmaService.updateCriteria(sopId, criteriaDetail.id, {
        reason: criteriaReasonDraft.trim() ? criteriaReasonDraft.trim() : null,
      });
      setCriteria((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setCriteriaDetail(updated);
      showToast("Success", "Image context saved.", "success");
    } catch (err) {
      setGlobalError(
        err instanceof Error ? err.message : "Failed to save image context.",
      );
    } finally {
      setCriteriaReasonBusy(false);
    }
  }, [criteriaDetail, criteriaReasonDraft, showToast, sopId]);

  const requestCloseCriteriaDetail = useCallback(
    (source: "dismiss" | "button") => {
      if (!criteriaDetail) return;
      if (source === "dismiss" && hasCriteriaReasonChanges) {
        setDiscardConfirmTarget("detail");
        return;
      }
      setCriteriaDetail(null);
    },
    [criteriaDetail, hasCriteriaReasonChanges],
  );

  const deleteCriteria = useCallback(
    async (criteriaId: string) => {
      if (!sopId) return;
      setCriteriaBusy(true);
      try {
        await rmaService.deleteCriteria(sopId, criteriaId);
        setCriteria((prev) => prev.filter((c) => c.id !== criteriaId));
        if (criteriaDetail?.id === criteriaId) setCriteriaDetail(null);
      } catch (err) {
        setGlobalError(
          err instanceof Error ? err.message : "Failed to delete criteria.",
        );
      } finally {
        setCriteriaBusy(false);
      }
    },
    [sopId, criteriaDetail?.id],
  );

  const confirmDeleteCriteria = useCallback(async () => {
    if (!deleteConfirmTargetId) return;
    await deleteCriteria(deleteConfirmTargetId);
    setDeleteConfirmTargetId(null);
  }, [deleteConfirmTargetId, deleteCriteria]);

  if (isRoleResolving) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }
  if (!canManageRMA) {
    return <Navigate to="/sop-dashboard" replace />;
  }

  const sopTitle =
    sops?.find((s) => s.sopId === sopId)?.getDisplayTitle() ?? sopId ?? "SOP";

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />

      <main className="pt-20 px-4 pb-16">
        <div className="max-w-5xl mx-auto pt-8 space-y-6">
          <button
            type="button"
            onClick={() => navigate(`/sop/${sopId}/rma`)}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analysis
          </button>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center shrink-0">
              <ScanSearch className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">RMA Criteria Dataset</h1>
              <p className="text-slate-400 text-sm mt-0.5">
                {sopTitle} — Manage pass/fail examples used by the RMA model
              </p>
            </div>
          </div>

          {globalError && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 flex items-center gap-2">
              <XCircle className="w-4 h-4 shrink-0" />
              <span className="flex-1">{globalError}</span>
              <button
                type="button"
                onClick={() => setGlobalError(null)}
                className="text-red-400 hover:text-red-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {!sopId && (
            <div className="rounded-2xl border border-slate-700/40 bg-slate-900/60 p-6">
              <p className="text-white font-semibold">No SOP linked</p>
              <p className="text-sm text-slate-400 mt-1">
                Link this RMA to an SOP to load the criteria dataset automatically.
              </p>
            </div>
          )}

          {criteriaLoading && (
            <div className="rounded-2xl border border-slate-700/40 bg-slate-900/60 px-6 py-5 flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
              <p className="text-sm text-slate-300">Loading criteria dataset...</p>
            </div>
          )}

          {criteriaError && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-5">
              <p className="text-sm text-red-300">{criteriaError}</p>
              <button
                type="button"
                onClick={() => {
                  if (!sopId) return;
                  setCriteriaLoading(true);
                  rmaService
                    .getCriteria(sopId)
                    .then((res) => setCriteria(res))
                    .catch((err) =>
                      setCriteriaError(
                        err instanceof Error
                          ? err.message
                          : "Failed to load criteria dataset.",
                      ),
                    )
                    .finally(() => setCriteriaLoading(false));
                }}
                className="mt-3 px-4 py-2 rounded-xl border border-red-500/30 text-red-200 hover:border-red-400/60 transition-colors text-sm"
              >
                Retry
              </button>
            </div>
          )}

          {!criteriaLoading && !criteriaError && sopId && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedCriteriaVerdict("pass")}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                      selectedCriteriaVerdict === "pass"
                        ? "bg-green-500/20 text-green-300 border border-green-500/40"
                        : "bg-slate-800/40 text-slate-300 border border-slate-700/50"
                    }`}
                  >
                    ({passCriteria.length}) Acceptable
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedCriteriaVerdict("fail")}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                      selectedCriteriaVerdict === "fail"
                        ? "bg-red-500/20 text-red-300 border border-red-500/40"
                        : "bg-slate-800/40 text-slate-300 border border-slate-700/50"
                    }`}
                  >
                    ({failCriteria.length}) Non-acceptable
                  </button>
                </div>
                {canEditDataset && (
                  <button
                    type="button"
                    onClick={openAddCriteriaModal}
                    disabled={criteriaBusy}
                    className="px-3 py-2 rounded-xl border border-slate-600/60 text-sm text-slate-200 hover:text-white hover:border-slate-500 transition-colors disabled:opacity-50"
                  >
                    Add Sample
                  </button>
                )}
              </div>

              <CriteriaDatasetColumn
                verdict={selectedCriteriaVerdict}
                items={visibleCriteria}
                pageSize={50}
                visibleCount={selectedCriteriaVerdict === "pass" ? passVisible : failVisible}
                onDelete={(id) => setDeleteConfirmTargetId(id)}
                onOpen={setCriteriaDetail}
                onShowMore={() =>
                  selectedCriteriaVerdict === "pass"
                    ? setPassVisible((n) => n + 50)
                    : setFailVisible((n) => n + 50)
                }
              />
            </div>
          )}
        </div>
      </main>

      {criteriaDetail && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-3 flex items-center justify-center"
          onClick={() => requestCloseCriteriaDetail("dismiss")}
        >
          <div
            className="bg-slate-900 border border-slate-700/60 rounded-2xl w-full max-w-2xl max-h-[78vh] overflow-hidden shadow-2xl flex flex-col mx-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => requestCloseCriteriaDetail("dismiss")}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-slate-700 text-slate-200 hover:text-white flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="bg-black flex items-center justify-center p-3 min-h-[180px] max-h-[36vh]">
              {criteriaDetail.url ? (
                <img
                  src={criteriaDetail.url}
                  alt="criteria detail"
                  className="max-w-full max-h-[32vh] object-contain"
                />
              ) : (
                <div className="w-full h-[220px] bg-black flex items-center justify-center">
                  <ImagePlus className="w-10 h-10 text-slate-500" />
                </div>
              )}
            </div>
            <div className="p-4 overflow-auto border-t border-slate-700/50">
              <div className="flex items-center gap-2 mb-3">
                {criteriaDetail.verdict === "pass" ? (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <span
                  className={`font-bold text-lg ${
                    criteriaDetail.verdict === "pass" ? "text-green-400" : "text-red-400"
                  }`}
                >
                  {criteriaDetail.verdict === "pass" ? "PASS example" : "FAIL example"}
                </span>
                {canEditDataset && sopId && (
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmTargetId(criteriaDetail.id)}
                    disabled={criteriaBusy}
                    className="ml-auto w-8 h-8 rounded-lg bg-red-500/15 text-red-200 border border-red-500/30 hover:border-red-400/60 transition-colors disabled:opacity-50 flex items-center justify-center"
                    aria-label="Delete criteria"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <label className="block text-xs text-slate-400 mb-1.5">
                Context linked to this image
              </label>
              <textarea
                rows={4}
                value={criteriaReasonDraft}
                onChange={(e) => setCriteriaReasonDraft(e.target.value)}
                disabled={criteriaReasonBusy}
                placeholder="Describe why this image is acceptable/non-acceptable..."
                className="w-full bg-slate-800/70 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50 resize-none"
              />
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => requestCloseCriteriaDetail("button")}
                  disabled={!hasCriteriaReasonChanges || criteriaReasonBusy}
                  className="px-4 py-2 rounded-xl border border-slate-600/50 text-slate-300 hover:text-white hover:border-slate-500 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveCriteriaReason}
                  disabled={criteriaReasonBusy || !canEditDataset || !hasCriteriaReasonChanges}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white text-sm font-semibold transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50"
                >
                  {criteriaReasonBusy ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {addCriteriaModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-3 flex items-center justify-center"
          onClick={() => requestCloseAddCriteriaModal("dismiss")}
        >
          <div
            className="bg-slate-900 border border-slate-700/60 rounded-2xl w-full max-w-2xl max-h-[78vh] overflow-hidden shadow-2xl flex flex-col mx-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => requestCloseAddCriteriaModal("dismiss")}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-slate-700 text-slate-200 hover:text-white flex items-center justify-center transition-colors"
              aria-label="Close"
              disabled={addCriteriaBusy}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-4 border-b border-slate-700/50">
              <div className="flex items-center justify-between gap-2 pr-10">
                <h3 className="text-base font-semibold text-white">
                  New {selectedCriteriaVerdict === "pass" ? "Acceptable" : "Non-acceptable"} Sample
                </h3>
              </div>
            </div>

            <div className="p-4 overflow-auto space-y-3">
              {pendingCriteriaUploads.length === 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => criteriaInputRef.current?.click()}
                    disabled={addCriteriaBusy}
                    className="rounded-xl border border-dashed border-slate-600/50 bg-slate-800/30 p-6 text-center w-full h-40"
                  >
                    <Upload className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                    Select images
                  </button>
                  <input
                    ref={criteriaInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES.join(",")}
                    multiple
                    className="hidden"
                    onChange={(e) => e.target.files && appendCriteriaFiles(e.target.files)}
                  />
                </>
              ) : (
                pendingCriteriaUploads.map((item) => (
                  <div
                    key={item.localId}
                    className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col gap-1">
                        <img
                          src={item.previewUrl}
                          alt={item.file.name}
                          className="w-40 h-40 rounded-lg object-cover border border-slate-700/50"
                        />
                        <p className="text-xs text-slate-400 truncate max-w-40 ml-1">
                          {item.file.name}
                        </p>
                      </div>

                      <div className="flex-1 min-w-0">
                        <label className="block text-xs mb-1.5">
                          Describe why this image is{" "}
                          {selectedCriteriaVerdict === "pass" ? "acceptable" : "non-acceptable"}
                          .
                          <textarea
                            rows={8}
                            value={item.context}
                            onChange={(e) =>
                              setPendingCriteriaUploads((prev) =>
                                prev.map((p) =>
                                  p.localId === item.localId
                                    ? { ...p, context: e.target.value }
                                    : p,
                                ),
                              )
                            }
                            placeholder={
                              selectedCriteriaVerdict === "pass"
                                ? "Scratch should be under 2mm."
                                : "Scratch is over 2mm and reaches copper."
                            }
                            className="mt-2 w-full h-full bg-slate-800/70 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50 resize-none"
                            disabled={addCriteriaBusy}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t border-slate-700/50 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => requestCloseAddCriteriaModal("button")}
                className="px-4 py-2 rounded-xl border border-slate-600/50 text-slate-300 hover:text-white hover:border-slate-500 transition-colors text-sm"
                disabled={addCriteriaBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={savePendingCriteriaUploads}
                disabled={!canSavePendingCriteriaUploads}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white text-sm font-semibold transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50"
              >
                {addCriteriaBusy ? "Saving..." : "Save images"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteConfirmTargetId)}
        onClose={() => setDeleteConfirmTargetId(null)}
        onConfirm={confirmDeleteCriteria}
        title="Delete sample?"
        description="This image and its context will be removed from the dataset."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={criteriaBusy}
      />

      <ConfirmDialog
        open={discardConfirmTarget !== null}
        onClose={() => setDiscardConfirmTarget(null)}
        onConfirm={() => {
          if (discardConfirmTarget === "add") closeAddCriteriaModal();
          if (discardConfirmTarget === "detail") setCriteriaDetail(null);
          setDiscardConfirmTarget(null);
        }}
        title="Discard unsaved changes?"
        description="You have unsaved changes. If you leave now, your edits will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        variant="danger"
      />

      <Toast
        open={toast.open}
        onOpenChange={hideToast}
        title={toast.title ?? ""}
        description={toast.description}
        type={toast.type}
        duration={toast.duration}
      />
    </div>
  );
};

export default RMADatasetPage;
